/**
 * Dashboard — the club's landing page for coaches and analysts.
 *
 * Shows what matters on arrival: recent matches with scores, data
 * processing status at a glance, and quick paths into the work.
 *
 * All data comes from two existing endpoints the page already had access
 * to: /api/v1/fixtures/fixtures (professional clubs with Sportmonks) or
 * /api/v1/fixtures/games (everyone else), plus /api/v1/workbench/games
 * for processing status. No new API surface.
 */
import { ArrowRight, BarChart3, Film, ListVideo, Radar, Upload } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'

import type { Session } from '../lib/api'
import { ApiError, api } from '../lib/api'
import { pathSeg } from '../lib/urls'
import WorthALook from '../components/WorthALook'
import MatchDate, { matchDay } from './MatchDate'
import MatchScore, { type LabeledScore, type Score } from './MatchScore'
import './admin.css'
import './dashboard.css'
// The recent-matches table is the Matches page's table (gm-*): its styles —
// pointer and hover on openable rows, film-less rows dimmed — live in
// games.css. Without this import the table rendered unstyled here, and an
// unopenable row was indistinguishable from an openable one.
import './games.css'

/* ── types ─────────────────────────────────────────────────────────────── */

type Fixture = {
  game_code: string | null
  gamefile_id: number | null
  home_team: string | null
  away_team: string | null
  home_team_logo: string | null
  away_team_logo: string | null
  date: string | null
  game_id: number | null
  home_score: number | null
  away_score: number | null
  /** Staff only: what the GOAL labels add up to (services/match_score). */
  goal_score?: LabeledScore | null
  /** The match date alone (YYYY-MM-DD) or null; `date` falls back to the upload. */
  match_date?: string | null
  has_video?: boolean
}

type WbGame = {
  code: string
  date: string | null
  video_status: string | null
  has_video: boolean
  tracking_status: string | null
  source: string
  has_tracking_file: boolean
  total_frames: number | null
  organization_id: number | null
}

/* ── helpers ───────────────────────────────────────────────────────────── */

/** pathSeg keeps the code one path segment and leaves its '@' readable. */
const gameHref = (code: string) => `/match-room/${pathSeg(code)}`


/** The list's day label: MatchDate.matchDay (a calendar day, no timezone shift). */
const matchDate = matchDay

/* ── small components ──────────────────────────────────────────────────── */

function TeamCell({ name, logo }: { name: string | null; logo: string | null }) {
  const [broken, setBroken] = useState(false)
  return (
    <span className="gm-team">
      {logo && !broken
        ? <img className="gm-team__logo" src={logo} alt="" loading="lazy"
               onError={() => setBroken(true)} />
        : <span className="gm-team__logo gm-team__logo--none" aria-hidden />}
      <span className="gm-team__name">{name ?? '—'}</span>
    </span>
  )
}

function StatCard({ label, value, sub }: {
  label: string; value: number | string; sub?: string
}) {
  return (
    <div className="dash-stat">
      <span className="dash-stat__value">{value}</span>
      <span className="dash-stat__label">{label}</span>
      {sub && <span className="dash-stat__sub">{sub}</span>}
    </div>
  )
}

/* ── main component ────────────────────────────────────────────────────── */

export default function Dashboard({ session }: { session: Session }) {
  // In-app hops go through the router; see Games.tsx.
  const navigate = useNavigate()
  const [fixtures, setFixtures] = useState<Fixture[] | null>(null)
  const [wbGames, setWbGames] = useState<WbGame[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Priori staff enter scores by hand for now (MatchScore).
  const canEditScore = !!(session.user.is_staff || session.user.is_platform_admin)
  const setScore = (gamefileId: number | null, sc: Score) =>
    setFixtures((all) => all && all.map((f) => (f.gamefile_id === gamefileId
      ? { ...f, home_score: sc.home, away_score: sc.away } : f)))
  const setDate = (gamefileId: number | null, d: string | null) =>
    setFixtures((all) => all && all.map((f) => (f.gamefile_id === gamefileId
      ? { ...f, match_date: d, date: d ?? f.date } : f)))

  // Sportmonks path disabled at cutover (Ben, 2026-09-10) — see Games.tsx.
  const endpoint = '/api/v1/fixtures/games'

  useEffect(() => {
    // Fetch both in parallel — they are independent.
    api<Record<string, unknown>>(endpoint)
      .then((body) => {
        const list = Object.values(body).find(Array.isArray) as Fixture[] | undefined
        setFixtures(list ?? [])
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)))

    api<{ games: WbGame[] }>('/api/v1/workbench/games')
      .then((d) => setWbGames(d.games))
      .catch(() => { /* workbench is supplementary; fixture data is enough */ })
  }, [endpoint])

  // Recent matches: newest first, capped at 5.
  const recent = useMemo(() => {
    if (!fixtures) return []
    return [...fixtures]
      .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''))
      .slice(0, 5)
  }, [fixtures])

  // Processing stats from workbench data.
  // Matches and Video ready count the MATCH LIST (the subtitle's number,
  // and each row's has_video — GameFile.has_video); 'complete' was never a
  // status anything wrote, so Video ready read 0 on every club. Tracking
  // ready still comes from the workbench, over its own count.
  const stats = useMemo(() => {
    if (!fixtures || !wbGames) return null
    const total = fixtures.length
    const withVideo = fixtures.filter((f) => f.has_video).length
    const tracked = wbGames.length
    const withTracking = wbGames.filter((g) =>
      g.tracking_status === 'complete' || g.has_tracking_file).length
    return { total, withVideo, tracked, withTracking }
  }, [fixtures, wbGames])

  const club = session.org?.name ?? 'Your club'

  if (error) {
    return (<><h1 className="page-title">Dashboard</h1><p className="error">{error}</p></>)
  }
  if (!fixtures) return <p className="muted">Loading…</p>

  const withFilm = fixtures.filter((f) => f.gamefile_id).length

  return (
    <>
      <h1 className="page-title">{club}</h1>
      <p className="page-sub">
        {fixtures.length} match{fixtures.length === 1 ? '' : 'es'}
        {withFilm > 0 && <> · {withFilm} with film</>}
      </p>

      {/* ── stats row ──────────────────────────────────────────────── */}
      {stats && stats.total > 0 && (
        <div className="dash-stats">
          <StatCard label="Matches" value={stats.total} />
          <StatCard label="Video ready" value={stats.withVideo}
                    sub={stats.total > 0
                      ? `${Math.round(100 * stats.withVideo / stats.total)}%`
                      : undefined} />
          <StatCard label="Tracking ready" value={stats.withTracking}
                    sub={stats.tracked > 0
                      ? `${Math.round(100 * stats.withTracking / stats.tracked)}%`
                      : undefined} />
        </div>
      )}

      <WorthALook />

      {/* ── recent matches ─────────────────────────────────────────── */}
      {recent.length > 0 && (
        <section className="dash-section">
          <div className="dash-section__head">
            <h2 className="dash-section__title">Recent matches</h2>
            <Link className="dash-section__link" to="/match-room">
              All matches <ArrowRight size={12} />
            </Link>
          </div>
          <table className="gm-table">
            <thead>
              <tr>
                <th>Home</th>
                <th>Away</th>
                <th>Date</th>
                <th className="gm-c">Score</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((f, i) => {
                const openable = !!f.gamefile_id && !!f.game_code
                const href = f.game_code ? gameHref(f.game_code) : undefined
                const cells = (
                  <>
                    <td><TeamCell name={f.home_team} logo={f.home_team_logo} /></td>
                    <td><TeamCell name={f.away_team} logo={f.away_team_logo} /></td>
                    <td className="gm-date">
                      <MatchDate gamefileId={f.gamefile_id} matchDate={f.match_date ?? null}
                                 label={matchDate(f.date)} canEdit={canEditScore}
                                 onSaved={(d) => setDate(f.gamefile_id, d)} />
                    </td>
                    <td className="gm-c">
                      <MatchScore gameId={f.game_id} canEdit={canEditScore}
                                  score={{ home: f.home_score, away: f.away_score }}
                                  labeled={f.goal_score}
                                  onSaved={(sc) => setScore(f.gamefile_id, sc)} />
                    </td>
                  </>
                )
                return openable ? (
                  <tr key={f.gamefile_id ?? i} className="gm-row is-openable"
                      onClick={() => navigate(href!)}
                      tabIndex={0} role="link"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          navigate(href!)
                        }
                      }}>
                    {cells}
                  </tr>
                ) : (
                  <tr key={f.gamefile_id ?? `x${i}`} className="gm-row"
                      title="No film for this match">
                    {cells}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </section>
      )}

      {/* ── quick actions — the rail's order: Review, Analysis, Setup ── */}
      <div className="dash-cards">
        <Link className="dash-card" to="/match-room">
          <h3><BarChart3 size={14} /> All matches</h3>
          <p>Every match with its score and date — the full list.</p>
        </Link>
        <Link className="dash-card" to="/clip-room">
          <h3><ListVideo size={14} /> Clip Room</h3>
          <p>Your club’s saved reels, and clips cut from any match’s data.</p>
        </Link>
        {session.home_is_feed && (
          <Link className="dash-card" to="/insights">
            <h3><Radar size={14} /> Insights Feed</h3>
            <p>Match stories and tactical reports prepared for your club.</p>
          </Link>
        )}
        <Link className="dash-card" to="/upload-match">
          <h3><Upload size={14} /> Upload Match</h3>
          <p>Send video and tracking data through the processing pipeline.</p>
        </Link>
        {/* Staff only: /pipeline is Pipeline Operations, the staff screen the
            rail deliberately keeps out of a club user's reach (nav.test.ts).
            A dashboard card handing a club that door undid the rail's
            decision. A club-facing processing-status surface is a follow-up,
            not this card. */}
        {(session.user.is_staff || session.user.is_platform_admin)
          && stats && stats.total > 0 && (
          <Link className="dash-card" to="/pipeline">
            <h3><Film size={14} /> Processing pipeline</h3>
            <p>Video and tracking data status across all your games.</p>
          </Link>
        )}
      </div>
    </>
  )
}
